import {
  GamificationStatus,
  type IGamificationCampaignDto,
  type Mechanic,
  normalizeMechanic,
} from "@/@types/gamification";

import {
  type CampaignPriority,
  priorityLevelOf,
} from "../constants/priority-levels";
import { mapStatusToFrontend } from "./status-mapper";

export const COMPETING_STATUSES: readonly GamificationStatus[] = [
  GamificationStatus.ATIVA,
  GamificationStatus.AGENDADA,
  GamificationStatus.RASCUNHO,
];

export interface CompetingCampaign {
  id: number;
  name: string;
  mechanic: Mechanic;
  status: GamificationStatus;
  startDate: string;
  endDate: string;
  priority: CampaignPriority;
  createdAt: string;
}

export interface CompetingCampaignsInput {
  campaigns: readonly IGamificationCampaignDto[];
  campaignType: Mechanic;
  priority: unknown;
  competes: boolean;
  group: string | null | undefined;

  currentCampaignId?: number;
  currentCreatedAt?: string;
}

export interface CampaignRankPosition {
  rank: number;
  total: number;
}

export interface CompetingCampaignsResult {
  sameCustomer: CompetingCampaign[];
  sameGame: CompetingCampaign[];
  position: CampaignRankPosition;
}

interface RankCandidate {
  priority: CampaignPriority;
  createdAtMs: number;
  id: number;
}

const toIsoDay = (date: string): string => date.split("T")[0];

const toCompetingCampaign = (
  dto: IGamificationCampaignDto,
): CompetingCampaign => ({
  id: dto.id,
  name: dto.campaignName,
  mechanic: normalizeMechanic(dto.mechanic ?? dto.campaignType),
  status: mapStatusToFrontend(dto.status, dto.deletedAt ?? null),
  startDate: toIsoDay(dto.startDate),
  endDate: toIsoDay(dto.endDate),
  priority: priorityLevelOf(dto.mechanicConfig?.priority),
  createdAt: dto.createdAt,
});

const normalizeGroup = (group: string | null | undefined): string =>
  (group ?? "").trim();

const timestampOf = (date: string | undefined): number => {
  const parsed = date ? Date.parse(date) : Number.NaN;
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const toRankCandidate = (campaign: CompetingCampaign): RankCandidate => ({
  priority: campaign.priority,
  createdAtMs: timestampOf(campaign.createdAt),
  id: campaign.id,
});

export function compareByPriority(a: RankCandidate, b: RankCandidate): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  if (a.createdAtMs !== b.createdAtMs) return b.createdAtMs - a.createdAtMs;
  return b.id - a.id;
}

function sortByPriority(campaigns: CompetingCampaign[]): CompetingCampaign[] {
  return [...campaigns].sort((a, b) =>
    compareByPriority(toRankCandidate(a), toRankCandidate(b)),
  );
}

function isCompetingCandidate(
  campaign: CompetingCampaign,
  currentCampaignId: number | undefined,
): boolean {
  if (campaign.id === currentCampaignId) return false;
  return COMPETING_STATUSES.includes(campaign.status);
}

function currentCandidate(input: CompetingCampaignsInput): RankCandidate {
  const hasOwnCreation = Boolean(input.currentCreatedAt);
  return {
    priority: priorityLevelOf(input.priority),
    createdAtMs: hasOwnCreation
      ? timestampOf(input.currentCreatedAt)
      : Number.POSITIVE_INFINITY,
    id: input.currentCampaignId ?? Number.POSITIVE_INFINITY,
  };
}

function rankOf(
  current: RankCandidate,
  rivals: readonly CompetingCampaign[],
): CampaignRankPosition {
  const ahead = rivals.filter(
    (rival) => compareByPriority(toRankCandidate(rival), current) < 0,
  ).length;
  return { rank: ahead + 1, total: rivals.length + 1 };
}

export function classifyCompetingCampaigns(
  input: CompetingCampaignsInput,
): CompetingCampaignsResult {
  const candidates = input.campaigns
    .map(toCompetingCampaign)
    .filter((campaign) => isCompetingCandidate(campaign, input.currentCampaignId));

  const group = normalizeGroup(input.group);
  const sameGroupById = new Set<number>(
    input.campaigns
      .filter((dto) => dto.mechanicConfig?.concurrency?.competes === true)
      .filter((dto) => normalizeGroup(dto.mechanicConfig?.concurrency?.group) === group)
      .map((dto) => dto.id),
  );

  const competesInGroup = input.competes && group !== "";
  const sameCustomer = competesInGroup
    ? candidates.filter((campaign) => sameGroupById.has(campaign.id))
    : [];
  const sameGame = sortByPriority(
    candidates.filter((campaign) => campaign.mechanic === input.campaignType),
  );

  return {
    sameCustomer: sortByPriority(sameCustomer),
    sameGame,
    position: rankOf(currentCandidate(input), sameGame),
  };
}
